import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card,
    CardBody,
    Chip,
    Typography,
    Button,
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSyncAlt, 
    faPlus, 
    faChartLine, 
    faLink, 
    faUniversity, 
    faArrowUp, 
    faArrowDown,
    faMoneyBillWave,
    faCog,
    faFileAlt,
    faFileDownload
} from '@fortawesome/free-solid-svg-icons';
import CashbookEntries from './CashbookEntries';
import CashbookImportModal from './CashbookImportModal';
import IncomeReport from './IncomeReport';
import CashbookGLMapping from './CashbookGLMapping';
import Select from 'react-select';
import {
    getMpesaWalletPresentation,
} from './mpesaWalletHelpers';

const { rootUrl } = require('../../../config/config');

const CashbookDashboard = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';
    const selectedAccountIdFromQuery = searchParams.get('accountId');

    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('entries');
    const [showImport, setShowImport] = useState(false);
    const selectedAccountIdRef = useRef(selectedAccountIdFromQuery);

    const accountLookup = useMemo(() => {
        return accounts.reduce((lookup, account) => {
            lookup[String(account.id)] = account;
            return lookup;
        }, {});
    }, [accounts]);

    const fetchAccounts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${rootUrl}/api/cashbook/accounts`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            const data = response.data?.success ? response.data.data : (Array.isArray(response.data) ? response.data : []);
            const nextSelected = data.find((acc) => String(acc.id) === String(selectedAccountIdRef.current)) || data[0] || null;
            setAccounts(data);
            setSelectedAccount(nextSelected);
            if (!nextSelected || String(nextSelected.id) !== String(selectedAccountIdRef.current)) {
                setBalance(null);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    useEffect(() => {
        selectedAccountIdRef.current = selectedAccount?.id || null;
    }, [selectedAccount]);

    useEffect(() => {
        selectedAccountIdRef.current = selectedAccountIdFromQuery || null;
    }, [selectedAccountIdFromQuery]);

    const fetchBalance = useCallback(async () => {
        if (!selectedAccount) return;

        try {
            const response = await axios.get(
                `${rootUrl}/api/cashbook/accounts/${selectedAccount.id}/balance`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                setBalance(response.data);
            } else {
                setBalance(null);
            }
        } catch (error) {
            setBalance(null);
            console.error('Error fetching balance:', error);
        }
    }, [authToken, selectedAccount]);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    useEffect(() => {
        if (selectedAccount) {
            fetchBalance();
        }
    }, [fetchBalance, selectedAccount]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const accountOptions = useMemo(() => {
        return accounts.map((acc) => {
            const mpesaPresentation = getMpesaWalletPresentation(acc, accountLookup, accounts);

            return {
                value: acc.id,
                label: mpesaPresentation.optionLabel || mpesaPresentation.label,
                ...acc,
                mpesaPresentation,
            };
        });
    }, [accounts, accountLookup]);

    const formatAccountOptionLabel = (option) => {
        const subtitle = option.account_type === 'MPESA'
            ? option.mpesaPresentation?.copy?.selectorSubtitle || option.mpesaPresentation?.subtitle
            : option.account_type;

        return (
            <div className="flex flex-col">
                <span className="font-semibold text-slate-700">{option.account_name}</span>
                <span className="text-xs text-slate-500">{subtitle}</span>
            </div>
        );
    };

    const selectedMpesaPresentation = useMemo(() => {
        return getMpesaWalletPresentation(selectedAccount, accountLookup, accounts);
    }, [selectedAccount, accountLookup, accounts]);

    const currentBalanceValue = Number(balance?.balance);
    const hasValidBalance = Number.isFinite(currentBalanceValue);
    const authHeaders = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);

    const tabs = [
        { id: 'entries', label: 'Entries', icon: faSyncAlt },
        { id: 'report', label: 'Income Report', icon: faChartLine },
        { id: 'mapping', label: 'GL Mapping', icon: faLink },
    ];

    return (
        <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Cashbook Dashboard</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Monitor balances, entries, and GL mapping from one cashbook workspace.
                    </p>
                </div>
            </div>

            <div className="w-full min-w-0 px-3 sm:px-4 lg:px-6 pb-8 space-y-6">
                <Card className="border border-[#eadfd8] shadow-sm rounded-xl bg-white">
                    <CardBody className="p-4 sm:p-5">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div>
                                <Typography className="text-base font-bold text-[#2a2e30]">Cashbook Workspace</Typography>
                                <Typography className="text-sm text-slate-500">
                                    Select an account, refresh balances, or add a new cashbook entry.
                                </Typography>
                            </div>

                            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 w-full lg:w-auto lg:justify-end">
                                <div className="min-w-0 sm:min-w-[240px] sm:flex-1 lg:flex-none">
                                        <Select
                                            options={accountOptions}
                                            value={accountOptions.find(opt => opt.value === selectedAccount?.id)}
                                            onChange={(opt) => {
                                                selectedAccountIdRef.current = opt?.id || null;
                                                setSelectedAccount(opt);
                                                if (opt?.id) {
                                                    setSearchParams({ accountId: String(opt.id) });
                                                } else {
                                                    setSearchParams({});
                                                }
                                            }}
                                            formatOptionLabel={formatAccountOptionLabel}
                                            className="react-select-container text-sm"
                                            classNamePrefix="react-select"
                                            placeholder="Select account..."
                                            isSearchable={false}
                                        />
                                </div>
                                <Button
                                    size="sm"
                                    onClick={fetchAccounts}
                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none flex items-center justify-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faSyncAlt} className={loading ? 'animate-spin' : ''} />
                                    Refresh
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => setShowImport(true)}
                                    className="bg-white border border-[#FF5733] hover:bg-orange-50 text-[#FF5733] text-sm font-semibold rounded-sm px-3 py-2 shadow-none flex items-center justify-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faFileDownload} />
                                    Import CSV
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => navigate('/cashbook/new')}
                                    className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none flex items-center justify-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    Add Entry
                                </Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                    <Card className="border border-[#f0d9cf] shadow-sm rounded-xl bg-white">
                        <CardBody className="p-5 flex items-start justify-between">
                            <div>
                                <Typography className="text-sm font-semibold text-slate-500">Current Balance</Typography>
                                <Typography className={`text-2xl font-bold mt-2 ${hasValidBalance ? 'text-[#2a2e30]' : 'text-slate-400'}`}>
                                    {hasValidBalance ? formatCurrency(balance.balance) : '--'}
                                </Typography>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <Typography className={`text-xs font-semibold ${hasValidBalance ? (currentBalanceValue >= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-slate-500'}`}>
                                        <FontAwesomeIcon icon={hasValidBalance ? (currentBalanceValue >= 0 ? faArrowUp : faArrowDown) : faUniversity} className="mr-1" />
                                        {selectedAccount?.account_type || 'No account selected'}
                                    </Typography>
                                    {selectedMpesaPresentation?.chips.map((chip) => (
                                        <Chip key={chip.label} value={chip.label} color={chip.color} size="sm" className="rounded-sm" />
                                    ))}
                                </div>
                                {selectedMpesaPresentation?.isMpesa && (
                                    <Typography className="mt-2 text-xs text-slate-500">
                                        {selectedMpesaPresentation.copy?.summaryText || selectedMpesaPresentation.subtitle}
                                    </Typography>
                                )}
                            </div>
                            <div className="h-11 w-11 rounded-xl bg-[#eef4ff] text-blue-600 flex items-center justify-center">
                                <FontAwesomeIcon icon={faUniversity} />
                            </div>
                        </CardBody>
                        {selectedAccount?.account_type === 'MPESA' && (
                            <div className="px-5 pb-4">
                                <button
                                    onClick={() => navigate(`/cashbook/mpesa-statement/${selectedAccount.id}`)}
                                    className="flex items-center gap-2 text-xs font-semibold text-[#FF5733] hover:underline"
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    View M-Pesa Statement
                                </button>
                            </div>
                        )}
                    </Card>

                    <Card className="border border-[#f0d9cf] shadow-sm rounded-xl bg-white">
                        <CardBody className="p-5 flex items-start justify-between">
                            <div>
                                <Typography className="text-sm font-semibold text-slate-500">Total Income</Typography>
                                <Typography className="text-2xl font-bold text-emerald-600 mt-2">
                                    {formatCurrency(balance?.total_income)}
                                </Typography>
                                <Typography className="text-xs text-slate-500 mt-2">Cumulative posted income</Typography>
                            </div>
                            <div className="h-11 w-11 rounded-xl bg-[#eefbf3] text-emerald-600 flex items-center justify-center">
                                <FontAwesomeIcon icon={faMoneyBillWave} />
                            </div>
                        </CardBody>
                    </Card>

                    <Card className="border border-[#f0d9cf] shadow-sm rounded-xl bg-white">
                        <CardBody className="p-5 flex items-start justify-between">
                            <div>
                                <Typography className="text-sm font-semibold text-slate-500">GL Integration</Typography>
                                <Typography className={`text-2xl font-bold mt-2 ${selectedAccount?.gl_account_code ? 'text-[#2a2e30]' : 'text-orange-600'}`}>
                                    {selectedAccount?.gl_account_code || 'No Mapping'}
                                </Typography>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('mapping')}
                                    className="text-xs text-[#FF5733] font-semibold mt-2 hover:underline"
                                >
                                    Open GL mapping
                                </button>
                            </div>
                            <div className="h-11 w-11 rounded-xl bg-[#fff1eb] text-[#FF5733] flex items-center justify-center">
                                <FontAwesomeIcon icon={faCog} />
                            </div>
                        </CardBody>
                    </Card>
                </div>

                <Card className="border border-[#eadfd8] shadow-sm rounded-xl bg-white overflow-hidden">
                    <CardBody className="p-0">
                        <div className="flex overflow-x-auto border-b border-gray-200 mb-0">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`px-6 py-3 font-semibold text-base transition-all border-b-2 whitespace-nowrap ${
                                        activeTab === tab.id ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600'
                                    }`}
                                >
                                    <span className="md:hidden">
                                        <FontAwesomeIcon icon={tab.icon} />
                                    </span>
                                    <span className="hidden md:inline">{tab.label}</span>
                                </button>
                            ))}
                        </div>

                        <div className="p-4 sm:p-5">
                            {activeTab === 'entries' && <CashbookEntries accountId={selectedAccount?.id} />}
                            {activeTab === 'report' && <IncomeReport />}
                            {activeTab === 'mapping' && <CashbookGLMapping />}
                        </div>
                    </CardBody>
                </Card>
            </div>

            <CashbookImportModal
                isOpen={showImport}
                onClose={() => setShowImport(false)}
                onCompleted={() => {
                    fetchAccounts();
                    fetchBalance();
                }}
                authHeaders={authHeaders}
            />
        </div>
    );
};

export default CashbookDashboard;
